import { Pagination, ReceivingArea } from '@ekkogmbh/apisdk';
import Permissions from 'src/Common/Permissions';
import { useCallback, useState } from 'react';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ReceivingAreaPanel } from './ReceivingAreaPanel';
import { Add, Delete, Edit } from '@mui/icons-material';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ReplenishmentPlanChip from 'src/ReplenishmentPlan/Components/ReplenishmentPlanChip';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

// Matches the ReplenishmentPlanChip height: chip height (48) + chip margin
// (8 top + 8 bottom) = 64px, so scroll indicators align across all columns.
const ROW_MIN_HEIGHT = 64;

const columns: ColumnDef[] = [
  {
    header: 'Name',
    body: (entry: ReceivingArea, searchText?: string) => (
      <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
        {searchText ? dataTableHighlight(entry.name ?? '', [searchText]) : entry.name}
      </ScrollableStack>
    ),
    filterField: 'name',
    sortField: 'RP.name',
    proportionalContent: '20%',
  },
  {
    header: 'Coordinate',
    body: (entry: ReceivingArea, searchText?: string) => (
      <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
        {searchText ? dataTableHighlight(entry.coordinate ?? '', [searchText]) : entry.coordinate}
      </ScrollableStack>
    ),
    filterField: 'coordinate',
    sortField: 'N.materializedPath',
    proportionalContent: '20%',
  },
  {
    header: 'Replenishment-Plan',
    body: (entry: ReceivingArea) => (
      <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
        <ReplenishmentPlanChip replenishmentPlan={entry.replenishmentPlan} />
      </ScrollableStack>
    ),
    proportionalContent: '60%',
  },
];

export const ReceivingAreaManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();

  const [, startLoading] = useRequest();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedArea, setSelectedArea] = useState<ReceivingArea>();

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedArea(undefined);
  }, []);

  const fetchAreas = useCallback((pagination: Pagination) => api.getReceivingAreas(pagination), [api]);

  const deleteArea = useCallback(() => {
    if (selectedArea === undefined) {
      return;
    }

    startLoading(
      async () => {
        await api.deleteReceivingArea(selectedArea);
      },
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api, selectedArea]);

  return (
    <ContentControl
      panel={<ReceivingAreaPanel receivingArea={selectedArea} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          title={'Delete Receiving Area'}
          text={selectedArea?.name ?? ''}
          type={'confirmation'}
          maxWidth={'sm'}
          onClose={dismiss}
          onConfirm={deleteArea}
          fullWidth
          centered
          open={isDialogOpen}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Receiving Area'}
          onClick={() => {
            dismiss();
            setSelectedArea(undefined);
            setPanelOpen(true);
          }}
          requiredPermission={Permissions.EKANBAN_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: ReceivingArea) => {
              dismiss();
              setSelectedArea(entry);
              setPanelOpen(true);
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: ReceivingArea) => {
              dismiss();
              setSelectedArea(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetchAreas}
      />
    </ContentControl>
  );
};
